import React, { useState, useEffect } from 'react';
import type { UserInputs, NameStyle, NameLength } from '../types';
import { NAME_STYLES, NAME_LENGTHS } from '../constants';
import { SpinnerIcon } from './icons/SpinnerIcon';
import { trackEvent } from '../src/gtag'; // import GA tracking function

interface GeneratorFormProps {
  onSubmit: (inputs: UserInputs) => void;
  isLoading: boolean;
  isLimitReached: boolean;
  initialIndustry?: string;
  initialKeywords?: string;
  initialStyle?: NameStyle;
}

export const GeneratorForm: React.FC<GeneratorFormProps> = ({
  onSubmit,
  isLoading,
  isLimitReached,
  initialIndustry = '',
  initialKeywords = '',
  initialStyle = 'Modern',
}) => {
  const [industry, setIndustry] = useState(initialIndustry);
  const [keywords, setKeywords] = useState(initialKeywords);
  const [style, setStyle] = useState<NameStyle>(initialStyle);
  const [length, setLength] = useState<NameLength>('Medium');
  const [errors, setErrors] = useState<{ industry?: string; keywords?: string }>({});

  useEffect(() => {
    setIndustry(initialIndustry);
  }, [initialIndustry]);

  useEffect(() => {
    setKeywords(initialKeywords);
  }, [initialKeywords]);

  useEffect(() => {
    setStyle(initialStyle);
  }, [initialStyle]);

  const validate = (): boolean => {
    const newErrors: { industry?: string; keywords?: string } = {};
    const industryRegex = /^[a-zA-Z0-9\s]+$/;
    const keywordsRegex = /^[a-zA-Z0-9,\s]*$/;

    if (!industry.trim()) {
      newErrors.industry = 'Industry is required.';
    } else if (!industryRegex.test(industry)) {
      newErrors.industry = 'Industry can only contain letters, numbers, and spaces.';
    }

    if (keywords.trim() && !keywordsRegex.test(keywords)) {
      newErrors.keywords = 'Keywords can only contain letters, numbers, commas, and spaces.';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) {
      return;
    }

    const inputs = { industry, keywords, style, length };

    // Track GA event
    trackEvent({
      action: 'generate_name',
      category: 'Brand Name Generator',
      label: `${industry}${keywords ? ` - ${keywords}` : ''}`,
    });

    // Call original submit
    onSubmit(inputs);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div>
        <label htmlFor="industry" className="block text-sm font-semibold text-gray-700 mb-2">
          Industry / Niche <span className="text-red-500">*</span>
        </label>
        <input
          type="text"
          id="industry"
          value={industry}
          onChange={(e) => {
            setIndustry(e.target.value);
            if (errors.industry) setErrors(prev => ({ ...prev, industry: undefined }));
          }}
          placeholder="e.g., Sustainable Fashion, Skincare Products"
          required
          className={`w-full px-4 py-3 border rounded-xl bg-gray-50 text-gray-900 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all outline-none ${errors.industry ? 'border-red-500' : 'border-gray-200'}`}
          aria-invalid={!!errors.industry}
          aria-describedby={errors.industry ? "industry-error" : undefined}
        />
        {errors.industry && <p id="industry-error" className="mt-2 text-sm text-red-600 flex items-center gap-1"><span className="w-1 h-1 rounded-full bg-red-600"></span>{errors.industry}</p>}
      </div>

      <div>
        <label htmlFor="keywords" className="block text-sm font-semibold text-gray-700 mb-2">
          Keywords (Optional)
        </label>
        <input
          type="text"
          id="keywords"
          value={keywords}
          onChange={(e) => {
            setKeywords(e.target.value);
            if (errors.keywords) setErrors(prev => ({ ...prev, keywords: undefined }));
          }}
          placeholder="e.g., eco, community, future, bright"
          className={`w-full px-4 py-3 border rounded-xl bg-gray-50 text-gray-900 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all outline-none ${errors.keywords ? 'border-red-500' : 'border-gray-200'}`}
          aria-invalid={!!errors.keywords}
          aria-describedby={errors.keywords ? "keywords-error" : undefined}
        />
        {errors.keywords && <p id="keywords-error" className="mt-2 text-sm text-red-600 flex items-center gap-1"><span className="w-1 h-1 rounded-full bg-red-600"></span>{errors.keywords}</p>}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label htmlFor="style" className="block text-sm font-semibold text-gray-700 mb-2">
            Style / Tone
          </label>
          <select
            id="style"
            value={style}
            onChange={(e) => setStyle(e.target.value as NameStyle)}
            className="w-full px-4 py-3 border border-gray-200 rounded-xl bg-gray-50 text-gray-900 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all outline-none"
          >
            {NAME_STYLES.map((s) => (
              <option key={s} value={s} className="bg-white">{s}</option>
            ))}
          </select>
        </div>
        <div>
          <label htmlFor="length" className="block text-sm font-semibold text-gray-700 mb-2">
            Name Length
          </label>
          <select
            id="length"
            value={length}
            onChange={(e) => setLength(e.target.value as NameLength)}
            className="w-full px-4 py-3 border border-gray-200 rounded-xl bg-gray-50 text-gray-900 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all outline-none"
          >
            {NAME_LENGTHS.map((l) => (
              <option key={l} value={l} className="bg-white">{l}</option>
            ))}
          </select>
        </div>
      </div>

      <div className="pt-4">
        <button
          type="submit"
          disabled={isLoading || isLimitReached || !industry}
          className="w-full flex justify-center items-center gap-2 py-4 px-6 bg-gradient-to-r from-[#4F46E5] to-[#6366F1] hover:from-[#4338CA] hover:to-[#4F46E5] text-white font-bold rounded-xl shadow-lg hover:shadow-xl hover:-translate-y-0.5 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none disabled:shadow-none text-lg"
        >
          {isLoading && <SpinnerIcon />}
          {isLoading ? 'Generating...' : 'Generate Names'}
        </button>
      </div>
    </form>
  );
};
